import { createElement, useState } from 'react';
import { lazyWithRetry } from './lazyWithRetry';

/**
 * lazyWithRetry plus `.preload()` (RD-LUI-016). A plain React.lazy suspends on
 * its first render EVEN WHEN its module is already cached, which blanked the page
 * and raised the boot splash on every in-app jump between pre-auth screens. Here
 * the first `.preload()` (or first render) starts ONE import; once it resolves,
 * every NEW mount renders the page synchronously, no suspend. An instance that
 * already mounted through the lazy path keeps it (never swaps type = never remounts).
 * Chunk-error recovery is unchanged: the import goes through importWithChunkRetry.
 */
export function lazyPreloadable(importer) {
  let status = 'idle';
  let loaded = null;
  let warming = null;
  const markReady = (mod) => { status = 'ready'; loaded = mod; return mod; };
  // Render path: the ONLY one with chunk-error recovery (lazyWithRetry wraps it in importWithChunkRetry).
  const Suspending = lazyWithRetry(() => (status === 'ready'
    ? Promise.resolve(loaded)
    : importer().then(markReady)));
  function Preloadable(props) {
    // Latched at mount: a ready module renders directly; otherwise stay on the lazy path.
    const [direct] = useState(() => status === 'ready');
    return direct ? createElement(loaded.default, props) : createElement(Suspending, props);
  }
  // Warm path: a bare import that swallows errors. It must NEVER retry or reload the tab
  // (a stale tab after a deploy would reload mid-form); the real render retries.
  Preloadable.preload = () => {
    if (status === 'ready' || warming) return;
    try {
      warming = Promise.resolve(importer()).then(markReady).catch(() => {}).then(() => { warming = null; });
    } catch (_e) { warming = null; }
  };
  return Preloadable;
}

export default lazyPreloadable;
